"use client"

import React from "react"
import { sendGTMEvent } from "@next/third-parties/google"
import Link from "next/link"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Shipment } from "./ui/svg/shipment"
import { Marketplace } from "./ui/svg/marketplace"
import { Store } from "./ui/svg/store"
import { cn } from "@/lib/utils"
import { ProductCardProps } from "@/types/product"

const circularFont = "var(--font-circular)"

export default function ProductCard({
  href,
  image,
  title,
  price,
  offerPrice,
  partNumber,
  position,
  storeID,
  badges,
  paymentPlan,
  deliveryStatus,
  mpSellerName,
  brand,
  moProBlo,
  searchTerm,
  page,
}: ProductCardProps) {
  const sendEvent = (action: string) => {
    const finalPrice =
      offerPrice && !isNaN(Number(offerPrice))
        ? offerPrice
        : price && !isNaN(Number(price))
          ? price
          : 0

    sendGTMEvent({
      ecommerce: {
        click: {
          actionField: {
            action: "click",
            list: `LP_Busqueda generativa|MO_PRO_BLO-${moProBlo}|${page.title.replace("|", "")}|${searchTerm}`,
          },
          products: [
            {
              id: partNumber,
              name: title,
              price: parseInt(finalPrice).toString(),
              brand: brand || "",
              category: "",
              variant: "",
              position: position,
            },
          ],
        },
      },
      nd1: "EEC - Promos - Seleccion - Lista de productos",
      nd2: `LP_Busqueda generativa|${page.title.replace("|", "")}`,
      nd3: `MO_PRO_BLO-${moProBlo}|TRK-1|${action}`,
      nd4: position,
      sellerID: partNumber.startsWith("MKP-") ? storeID?.toString() : "COP",
      promoTexto: title,
      promoCreativo: image.split("/").pop(),
      sku: partNumber,
      productoNombre: title,
      precioNormal: price ? parseInt(price).toString() : "0",
      precioDescuento: offerPrice ? parseInt(offerPrice).toString() : "0",
      event: "promosSeleccionLp",
    })
  }

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-[#e2e8f0] hover:shadow-card-hover hover:border-[#145b97] transition duration-300 ease-in-out flex flex-col h-full">
      <Link
        href={`https://www.coppel.com${href}`}
        onClick={() => sendEvent("Img pdp")}
        className="relative"
      >
        <picture>
          <source
            media="(min-width: 768px)"
            width="255"
            height="205"
            srcSet={`${image}?iresize=width:255,height:205 1x, ${image}?iresize=width:382,height:307 2x, ${image}?iresize=width:510,height:510 3x`}
          />
          <source
            media="(min-width: 320px)"
            width="195"
            height="155"
            srcSet={`${image}?iresize=width:195,height:155 1x, ${image}?iresize=width:292,height:232 2x, ${image}?iresize=width:390,height:390 3x`}
          />
          <img
            src={image + "?iresize=width:195,height:155"}
            alt={title}
            className={`object-contain aspect-[5/4] w-full`}
          />
        </picture>
      </Link>
      <div className="flex flex-wrap px-3 mt-2 min-h-[2rem]">
        {badges.map((badge: any, index: number) => (
          <div
            key={index}
            className={cn(
              "font-['Helvetica, sans-serif'] antialiased px-1 py-1 mr-1 text-xs rounded-lg max-h-[24px]",
              badge.group == "1"
                ? "bg-[#F1F4FA] text-[#194B7B]"
                : "bg-[#EBEBEB] text-black"
            )}
          >
            {badge.label}
          </div>
        ))}
      </div>
      <div className="px-3 pt-1 pb-3 flex-grow flex flex-col justify-between productcard-container">
        <div>
          <h3
            className="text-sm text-[#1b1a16] font-['Helvetica, sans-serif'] antialiased font-normal line-clamp-2 min-h-[2.5rem]"
            title={title}
          >
            <Link
              href={`https://www.coppel.com${href}`}
              onClick={() => sendEvent("Img pdp")}
            >
              {title}
            </Link>
          </h3>

          <p className="mt-2 flex items-baseline">
            {offerPrice !== 0 && (
              <span
                className={`${circularFont} text-[#d3273e] text-[19px] font-bold mr-2`}
              >
                ${Number(offerPrice).toLocaleString("en-US")}
              </span>
            )}
            <span
              className={cn(
                "text-sm",
                offerPrice === 0
                  ? `${circularFont} text-[19px] text-black font-bold`
                  : "text-[#717171] line-through"
              )}
            >
              ${Number(price).toLocaleString("en-US")}
            </span>
          </p>

          <p className="text-[#717171] text-[14px] antialiased font-normal mt-1">
            Desde ${paymentPlan.value} quincenal
          </p>
        </div>

        <div className="flex mt-2">
          <DeliveryIcon
            condition={deliveryStatus.shipment}
            Icon={Shipment}
            tooltipText="Envío a tu domicilio"
          />
          <DeliveryIcon
            condition={deliveryStatus.store}
            Icon={Store}
            tooltipText="Entrega en tienda"
          />
          <DeliveryIcon
            condition={deliveryStatus.marketPlace}
            Icon={Marketplace}
            tooltipText={`Vendido por ${mpSellerName}`}
          />
        </div>
      </div>
    </div>
  )
}

function DeliveryIcon({
  condition,
  Icon,
  tooltipText,
}: {
  condition: boolean
  Icon: React.ComponentType
  tooltipText: string
}) {
  if (!condition) return null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          className="mr-2"
          aria-label={`icon-${tooltipText.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Icon />
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-black text-white">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
